import queryString from 'query-string'
import React from 'react'

import {
  PanelDetails,
  PanelHeader,
  PanelTitle
} from 'components/attacks/details/AttackStyles'
import ApiTrigger from 'components/common/ApiTrigger'
import Button from 'components/common/Button'
import { InfoTooltip } from 'components/common/InfoTooltip'
import InputTextField from 'components/common/InputTextField'
import Section from 'components/common/Section'
import TextButton from 'components/common/TextButton'
import { MFATokenEntry } from 'components/profile/MFATokenEntry'
import useAgent from 'hooks/use-agent'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'
import { MFAResponse } from 'types/api/user/session'

enum MFA_ACTIONS {
  ENTER_PASSWORD = 'enterpw',
  ENTER_TOKEN = 'entertoken',
  DISABLE = 'disable',
  UPDATE_PASSWORD = 'updatepw',
  UPDATE_TOKEN = 'updatetoken',
  RESET = 'reset',
  RESET_AUTH_FIELDS = 'resetauth'
}

type MFAMode = 'disabled' | 'enabled' | 'password' | 'token' | 'disabling'

type MFAState = {
  mode: MFAMode
  password: string
  token: string
  mfaResponse: MFAResponse | undefined
}

type MFAAction =
  | { type: MFA_ACTIONS.RESET_AUTH_FIELDS }
  | { type: MFA_ACTIONS.UPDATE_PASSWORD; password: string }
  | { type: MFA_ACTIONS.UPDATE_TOKEN; token: string }
  | { type: MFA_ACTIONS.ENTER_PASSWORD }
  | { type: MFA_ACTIONS.ENTER_TOKEN; mfaResponse: MFAResponse }
  | { type: MFA_ACTIONS.RESET; enabled: boolean }
  | { type: MFA_ACTIONS.DISABLE }

const mfaReducer = (state: MFAState, action: MFAAction): MFAState => {
  switch (action.type) {
    case MFA_ACTIONS.RESET_AUTH_FIELDS:
      return {
        ...state,
        password: '',
        token: ''
      }

    case MFA_ACTIONS.UPDATE_PASSWORD:
      return {
        ...state,
        password: action.password
      }

    case MFA_ACTIONS.UPDATE_TOKEN:
      return {
        ...state,
        token: action.token
      }

    case MFA_ACTIONS.ENTER_PASSWORD:
      return {
        ...state,
        mode: 'password',
        password: '',
        token: '',
        mfaResponse: undefined
      }

    case MFA_ACTIONS.ENTER_TOKEN:
      return {
        ...state,
        mode: 'token',
        token: '',
        mfaResponse: action.mfaResponse
      }

    case MFA_ACTIONS.RESET:
      return {
        mode: action.enabled ? 'enabled' : 'disabled',
        password: '',
        token: '',
        mfaResponse: undefined
      }

    case MFA_ACTIONS.DISABLE:
      return {
        ...state,
        mode: 'disabling',
        password: '',
        token: ''
      }

    default:
      return state
  }
}

export const MFA: React.FC<{
  session: Record<string, any>
}> = ({ session }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const initialState: MFAState = {
    mode: 'disabled',
    password: '',
    token: '',
    mfaResponse: null
  }

  const [mfaState, dispatch] = React.useReducer(mfaReducer, initialState)

  const {
    data,
    doFetch: updateMfaStatus,
    isLoading: isLoadingMFA
  } = useAgent<MFAResponse>(
    '/users/auth/mfa/info',
    { enabled: 'false', provider: null },
    { shouldCache: false }
  )

  // memoize so effect below is triggered only when this changes
  const mfaEnabled = React.useMemo(() => {
    return data.enabled === 'true'
  }, [data])

  // update after loading
  React.useEffect(() => {
    if (!isLoadingMFA) {
      dispatch({ type: MFA_ACTIONS.RESET, enabled: mfaEnabled })
    }
  }, [isLoadingMFA, mfaEnabled, dispatch])

  const refresh = () => {
    // do an initial reset prior to loading so form returns to initial state
    dispatch({ type: MFA_ACTIONS.RESET, enabled: mfaEnabled })
    // refresh data from the API (to be sure we're displaying accurate data) and reset the form
    updateMfaStatus()
  }

  const handlePasswordChange = (event) => {
    dispatch({
      type: MFA_ACTIONS.UPDATE_PASSWORD,
      password: event.target.value
    })
  }

  const handleTokenChange = (event) => {
    dispatch({
      type: MFA_ACTIONS.UPDATE_TOKEN,
      token: event.target.value
    })
  }

  const { trans } = useTranslation()

  return (
    <Section isLoading={isSubmitting || isLoadingMFA}>
      <PanelHeader>
        <PanelTitle>Multi-factor authentication</PanelTitle>
        <InfoTooltip
          title={trans('profile.multi_factor_authentication_tooltip.title')}
        />
      </PanelHeader>
      <PanelDetails>
        {mfaState.mode === 'disabled' && (
          <section>
            <span>
              You currently do not have multi-factor authentication set
              up.&nbsp;
            </span>
            <a
              data-test-mfa-button="enable"
              onClick={() => {
                dispatch({ type: MFA_ACTIONS.ENTER_PASSWORD })
              }}
            >
              Enable multi-factor authentication.
            </a>
          </section>
        )}

        {mfaState.mode === 'password' && (
          <section>
            <section>
              You will need to enter your password in order to set up
              multi-factor authentication.
            </section>
            <section css={{ marginTop: theme.space[3] }}>
              <InputTextField
                data-test-password-input
                focusOnMount
                placeholder="Password"
                type="password"
                value={mfaState.password}
                onChange={handlePasswordChange}
              />
            </section>
            <section className="menu">
              <ApiTrigger
                data-test-enable-mfa-button
                as={Button}
                disabled={isSubmitting || mfaState.password.length < 1}
                errorToastMessage="There was an issue enabling multi-factor authentication. You may have entered the wrong password. Please try again."
                request={{
                  url: '/users/auth/mfa/enable',
                  method: 'POST',
                  data: queryString.stringify({
                    email: session.identifier,
                    password: mfaState.password
                  }),
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }}
                successToastMessage="" // we do not display a toast message here because the user is not finished yet
                onError={() => {
                  setIsSubmitting(false)
                  dispatch({
                    type: MFA_ACTIONS.UPDATE_PASSWORD,
                    password: ''
                  })
                }}
                onRequest={() => {
                  setIsSubmitting(true)
                }}
                onSuccess={({ response }) => {
                  setIsSubmitting(false)
                  dispatch({
                    type: MFA_ACTIONS.ENTER_TOKEN,
                    mfaResponse: response.data
                  })
                }}
              >
                Enable Multi-factor Authentication
              </ApiTrigger>
              <TextButton
                css={{ marginLeft: theme.space[3] }}
                onClick={refresh}
              >
                I&apos;ve changed my mind
              </TextButton>
            </section>
          </section>
        )}

        {mfaState.mode === 'token' && (
          <MFATokenEntry
            email={session.identifier}
            mfaResponse={mfaState.mfaResponse}
            validateMFASuccess={refresh}
            onCancel={refresh}
          />
        )}

        {mfaState.mode === 'enabled' && (
          <section>
            <section className="green">
              <span>Multi-factor authentication is enabled. &nbsp;</span>
              <a
                data-test-mfa-button="disable"
                onClick={() => {
                  dispatch({ type: MFA_ACTIONS.DISABLE })
                }}
              >
                Disable multi-factor authentication.
              </a>
            </section>
          </section>
        )}

        {mfaState.mode === 'disabling' && (
          <section>
            <section>
              <p className="red">
                You are now disabling multi-factor authentication.
              </p>
              <p>
                You will need to enter your password and a token from your
                current authenticator in order to disable multi-factor
                authentication.
              </p>
            </section>
            <section>
              <InputTextField
                data-test-password-input
                focusOnMount
                placeholder="Password"
                type="password"
                value={mfaState.password}
                onChange={handlePasswordChange}
              />
            </section>
            <section>
              <InputTextField
                data-test-token-input
                placeholder="Token"
                value={mfaState.token}
                onChange={handleTokenChange}
              />
            </section>

            <section className="menu">
              <ApiTrigger
                destructive
                as={Button}
                data-test-mfa-button="disable"
                disabled={
                  isSubmitting ||
                  mfaState.password.length < 1 ||
                  mfaState.token.length < 1
                }
                errorToastMessage="There was an issue disabling multi-factor authentication. You may have entered the wrong password or token. Please try again."
                request={{
                  url: '/users/auth/mfa/disable',
                  method: 'POST',
                  data: queryString.stringify({
                    email: session.identifier,
                    password: mfaState.password,
                    token: mfaState.token
                  }),
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                  }
                }}
                successToastMessage="" // we do not display a toast message here because the user is not finished yet
                onError={() => {
                  setIsSubmitting(false)
                  dispatch({ type: MFA_ACTIONS.RESET_AUTH_FIELDS })
                }}
                onRequest={() => {
                  setIsSubmitting(true)
                }}
                onSuccess={() => {
                  setIsSubmitting(false)
                  updateMfaStatus()
                }}
              >
                Disable Multi-factor Authentication
              </ApiTrigger>
              <TextButton
                css={{ marginLeft: theme.space[3] }}
                onClick={refresh}
              >
                I&apos;ve changed my mind.
              </TextButton>
            </section>
          </section>
        )}
      </PanelDetails>
    </Section>
  )
}
